/* eslint-disable @typescript-eslint/no-explicit-any */
import { IAdditiveEffectAlgorithm } from "../types/additiveAlgorithm";
import { SampleRecordFilterParams } from "../types/common";
import { ICompany } from "../types/company";
import { IUser } from "../types/user";
import {
  ISampleRecordBaseFields,
  ISampleRecordCreationPayload,
  ISampleRecordPopulated,
  SpectraDeviceNames,
} from "../types/sample";
import backendApi from "./backendApi";
import { showSnackbar } from "./snackbar.service";

const routePrefix = "datalake/sample-record";

export interface ISampleRecordUpdatePayload {
  isChemical?: boolean;
  fieldName: string;
  oldValue: any;
  newValue: any;
}

interface GetDataParams extends SampleRecordFilterParams {
  page?: number;
  pageSize?: number;
  onlySpectra?: boolean;
  notIncludeSpectra?: boolean;
}
export default {
  async saveSampleRecords({
    companyId,
    clientId,
    newRecords,
    dataField,
    fileName,
    groupName,
    groupDescription,
    updateReason,
    appliedAdditives,
    spectraDeviceName,
  }: {
    companyId: string;
    clientId?: string;
    newRecords: ISampleRecordCreationPayload[];
    dataField: string;
    fileName?: string;
    groupName?: string;
    groupDescription?: string;
    updateReason?: string;
    appliedAdditives?: IAdditiveEffectAlgorithm[];
    spectraDeviceName?: SpectraDeviceNames;
  }): Promise<any> {
    try {
      const result = await backendApi()?.post(`${routePrefix}/bulk-save`, {
        clientId,
        newRecords,
        companyId,
        dataField,
        fileName,
        groupName,
        groupDescription,
        updateReason,
        appliedAdditives,
        spectraDeviceName
      });
      if (result?.status != 200) {
        showSnackbar(
          "danger",
          "Error",
          result?.data.message || "Could not save the samples."
        );
        return null;
      }
      return result;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not save the samples."
        );
        return null;
      }
    }
  },

  async getSampleRecordsByIds(companyId: string, ids: string[]): Promise<any> {
    try {
      const result = await backendApi()?.post(`${routePrefix}/get-by-ids`, {
        companyId,
        ids,
      });
      if (result?.status != 200) {
        showSnackbar(
          "danger",
          "Error",
          result?.data.message || "Could not load those samples."
        );
        return null;
      }
      return result.data;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not load those samples."
        );
        return null;
      }
    }
  },

  async getData(params: GetDataParams): Promise<any> {
    try {
      const result = await backendApi()?.post(`${routePrefix}/get`, params);
      return result.data;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not load samples."
        );
        return null;
      }
    }
  },
  async searchSamplesBySampleId(
    companyId: string,
    searchTerm: string,
    includeChemicals?: boolean
  ): Promise<ISampleRecordPopulated[]> {
    try {
      const result = await backendApi()?.post(
        `${routePrefix}/search-sample-id`,
        { searchTerm, companyId, includeChemicals }
      );
      return result.data;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not search samples."
        );
      }
      return [];
    }
  },
  async getFieldsData(
    filters: GetDataParams & { fields: string[] },
    onlySamplesWithSpectra?: boolean,
    populateSpectraData?: boolean
  ): Promise<any> {
    try {
      const result = await backendApi()?.post(
        `${routePrefix}/get-fields-data`,
        { ...filters, onlySamplesWithSpectra, populateSpectraData }
      );
      return result.data;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not load sample fields."
        );
        return null;
      }
    }
  },
  async getNumberOfSamplesWithSpectra(filters: GetDataParams): Promise<number> {
    try {
      const result = await backendApi()?.post(
        `${routePrefix}/get-number-of-samples-with-spectra`,
        filters
      );
      return result.data;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not count samples with spectra."
        );
        throw error;
      } else {
        throw error;
      }
    }
  },
  async getLastSSIDs(companyId: string): Promise<any> {
    try {
      const result = await backendApi()?.get(
        `${routePrefix}/get-last-company-uid`,
        { params: { companyId } }
      );
      return result.data;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not load the latest sample IDs."
        );
        return null;
      }
    }
  },
  async checkSampleExistence(
    companyId: string,
    sampleNumbers: string[],
    dataField?: string
  ): Promise<any> {
    try {
      const result = await backendApi()?.post(
        `${routePrefix}/check-sample-existence`,
        { sampleNumbers, companyId, dataField }
      );
      return result.data;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not check whether these samples already exist."
        );
        return null;
      }
    }
  },
  async updateRecord(
    uid: number,
    updateData: ISampleRecordBaseFields,
    updatedFields?: ISampleRecordUpdatePayload[],
    reason?: string
  ) {
    try {
      const result = await backendApi()?.post(`${routePrefix}/update-record`, {
        uid,
        updateData,
        updatedFields,
        reason,
      });
      return result.data;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not update the sample."
        );
        return null;
      }
    }
  },
  async updateRecordProperty(uid: number, fieldName: string, newValue: any) {
    try {
      const result = await backendApi()?.post(
        `${routePrefix}/update-record-property`,
        {
          uid,
          fieldName,
          newValue,
        }
      );
      return result.data;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not update the sample."
        );
        return null;
      }
    }
  },
  async bulkUpdateRecordProperty(
    uids: number[],
    fieldName: string,
    newValue: any
  ) {
    try {
      const result = await backendApi()?.post(
        `${routePrefix}/bulk-update-record-property`,
        {
          uids,
          fieldName,
          newValue,
        }
      );
      return result.data;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not update the samples."
        );
        return null;
      }
    }
  },
  async bulkUpdateMultipleRecords(
    updateData: (Partial<ISampleRecordPopulated>)[],
    updateReason: string,
    appliedAdditives?: IAdditiveEffectAlgorithm[],
    spectraDeviceName?: SpectraDeviceNames
  ) {
    try {
      const result = await backendApi()?.post(
        `${routePrefix}/bulk-update-multiple-records`,
        {
          updateData,
          updateReason,
          appliedAdditives,
          spectraDeviceName
        }
      );
      return result;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not update the samples."
        );
        return null;
      }
    }
  },
  async delete(uids: number[]) {
    try {
      const result = await backendApi()?.post(`${routePrefix}/delete`, {
        uids,
      });
      return result.data;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not delete the samples."
        );
        return null;
      }
    }
  },
  async getPrediction(body: any) {
    try {
      const result = await backendApi()?.post(`${routePrefix}/get-prediction`, {
        ...(body || {}),
        selectedTasks: body?.task,
      });
      return result.data;
    } catch (error: any) {
      if (error?.response?.status) {
        throw error;
      }
    }
  },
  async getArchivedRecords(sampleNumber: string) {
    try {
      const result = await backendApi()?.post(
        `${routePrefix}/get-archived-records`,
        {
          sampleNumber,
        }
      );
      return result.data;
    } catch (error: any) {
      if (error?.response?.status) {
        throw error;
      }
    }
  },
  async getSpectraData(ids: string[]) {
    try {
      const result = await backendApi()?.post(
        `${routePrefix}/get-spectra-data`,
        {
          ids,
        }
      );
      return result.data;
    } catch (error: any) {
      if (error?.response?.status) {
        throw error;
      }
    }
  },
  async markAsReportMade(uids: number[]) {
    try {
      const result = await backendApi()?.post(
        `${routePrefix}/mark-report-made`,
        { uids }
      );
      return result.data;
    } catch (error: any) {
      if (error?.response?.status) {
        throw error;
      }
    }
  },
  async getReportMadeSamples({
    companyId,
    fromDate,
    toDate,
    page,
    pageSize,
  }: {
    companyId: string;
    fromDate?: Date;
    toDate?: Date;
    page?: number;
    pageSize?: number;
  }) {
    try {
      const result = await backendApi()?.post(
        `${routePrefix}/get-report-made-samples`,
        { companyId, fromDate, toDate, page, pageSize }
      );
      return result.data;
    } catch (error: any) {
      if (error?.response?.status) {
        throw error;
      }
    }
  },
  async getPeerMoveStatus(): Promise<{
    enabled: boolean;
    peerEnvironmentName?: string;
    currentEnvironmentName?: string;
  } | null> {
    try {
      const result = await backendApi()?.get(`${routePrefix}/peer-move-status`);
      return result?.data;
    } catch (error: any) {
      return null;
    }
  },
  async getPeerEnvironmentCompanies(): Promise<ICompany[]> {
    try {
      const result = await backendApi()?.get(
        `${routePrefix}/peer-environment/companies`
      );
      return result?.data || [];
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not load companies in the other environment."
        );
      }
      return [];
    }
  },
  async getPeerEnvironmentUsers(companyId: string): Promise<IUser[]> {
    try {
      const result = await backendApi()?.get(
        `${routePrefix}/peer-environment/users`,
        { params: { companyId } }
      );
      return result?.data || [];
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not load users in the other environment."
        );
      }
      return [];
    }
  },
  async moveSamplesToPeer({
    sampleIds,
    targetCompanyId,
    targetUserId,
  }: {
    sampleIds: string[];
    targetCompanyId: string;
    targetUserId: string;
  }) {
    try {
      const result = await backendApi()?.post(`${routePrefix}/move-to-peer`, {
        sampleIds,
        targetCompanyId,
        targetUserId,
      });
      return result;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not move the samples."
        );
      }
      return null;
    }
  },
  async countSamplesWithChemicalField(fieldName: string): Promise<{
    count: number;
    fieldName: string;
  } | null> {
    try {
      const result = await backendApi()?.post(
        `${routePrefix}/count-samples-with-chemical-field`,
        { fieldName }
      );
      return result.data;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not count samples with that chemical."
        );
      }
      return null;
    }
  },
  async unsetChemicalFieldFromSamples(fieldName: string): Promise<{
    modifiedCount: number;
    fieldName: string;
  } | null> {
    try {
      const result = await backendApi()?.post(
        `${routePrefix}/unset-chemical-field`,
        { fieldName }
      );
      return result.data;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not remove that chemical field."
        );
      }
      return null;
    }
  },
};
